import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import {
  BaseProviderType,
  CampaignTypeOptModel,
  ChannelOptModel,
  ChannelTypeOptModel,
  CustomTableOptModel,
  HSMTemplateOptModel,
  OrderStateOptModel,
  PermissionProviderType,
  PhoneExternalOptModel,
  TicketCellOptModel,
  TicketReasonOptModel,
  TicketStateOptModel,
  TicketSubReasonOptModel,
  UserAuditOptModel,
  UserOperatorOptModel,
} from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as ListService from "services/list";
import { aytyFormatError, sortBy } from "utils";

type ProjectListContextType = {
  loading: boolean;
  campaignTypeOpts?: CampaignTypeOptModel[];
  channelOpts?: ChannelOptModel[];
  channelTypeOpts?: ChannelTypeOptModel[];
  customTableOpts?: CustomTableOptModel[];
  hsmTemplateOpts?: HSMTemplateOptModel[];
  orderStateOpts?: OrderStateOptModel[];
  phoneExternalOpts?: PhoneExternalOptModel[];
  ticketCellOpts?: TicketCellOptModel[];
  ticketReasonOpts?: TicketReasonOptModel[];
  ticketStateOpts?: TicketStateOptModel[];
  ticketSubReasonOpts?: TicketSubReasonOptModel[];
  userAuditOpts?: UserAuditOptModel[];
  userOperatorOpts?: UserOperatorOptModel[];
};

const ProjectListContext = createContext({} as ProjectListContextType);

const Provider = ({ children }: BaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [campaignTypeOpts, setCampaignTypeOpts] =
    useState<CampaignTypeOptModel[]>();
  const [channelOpts, setChannelOpts] = useState<ChannelOptModel[]>();
  const [channelTypeOpts, setChannelTypeOpts] =
    useState<ChannelTypeOptModel[]>();
  const [customTableOpts, setCustomTableOpts] =
    useState<CustomTableOptModel[]>();
  const [hsmTemplateOpts, setHSMTemplateOpts] =
    useState<HSMTemplateOptModel[]>();
  const [orderStateOpts, setOrderStateOpts] = useState<OrderStateOptModel[]>();
  const [phoneExternalOpts, setPhoneExternalOpts] =
    useState<PhoneExternalOptModel[]>();
  const [ticketCellOpts, setTicketCellOpts] = useState<TicketCellOptModel[]>();
  const [ticketReasonOpts, setTicketReasonOpts] =
    useState<TicketReasonOptModel[]>();
  const [ticketStateOpts, setTicketStateOpts] =
    useState<TicketStateOptModel[]>();
  const [ticketSubReasonOpts, setTicketSubReasonOpts] =
    useState<TicketSubReasonOptModel[]>();
  const [userAuditOpts, setUserAuditOpts] = useState<UserAuditOptModel[]>();
  const [userOperatorOpts, setUserOperatorOpts] =
    useState<UserOperatorOptModel[]>();
  const { user, pIdProject } = useProfile();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getProjectLists = useCallback(async () => {
    if (pIdProject) {
      if (user?.hasAccessAttend)
        await Promise.all([
          ListService.getPhoneExternalOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.telephoneExternalList?.filter(
                (t) => t.IsActive,
              );
              setPhoneExternalOpts(sortBy(current, "NmTelephoneExternal"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
        ]).catch(errorsResolver.defaultError);

      if (user?.hasAccessAdmin || user?.hasAccessRegister)
        await Promise.all([
          ListService.getChannelOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.omniSourceList?.filter((o) => o.IsActive);
              setChannelOpts(sortBy(current, "NmOmniSource"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
        ]).catch(errorsResolver.defaultError);

      if (user?.hasAccessRegister)
        await Promise.all([
          ListService.getCampaignTypeOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.campaignTypeList?.filter((c) => c.IsActive);
              setCampaignTypeOpts(sortBy(current, "NmCampaignType"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getChannelTypeOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.channelTypeList?.filter((c) => c.IsActive);
              setChannelTypeOpts(sortBy(current, "NmChannelType"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getCustomTableOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) setCustomTableOpts(data.tableCustomList);
            else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getHSMTemplateOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0)
              setHSMTemplateOpts(
                sortBy(
                  data.messageOutboundTemplateList,
                  "NmMessageOutboundTemplate",
                ),
              );
            else warning({ description: t(aytyFormatError(data)) });
          }),
        ]).catch(errorsResolver.defaultError);

      if (user?.hasAccessRegister && user.hasTicketModule)
        await Promise.all([
          ListService.getTicketCellOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.ticketCellList?.filter((t) => t.IsActive);
              setTicketCellOpts(sortBy(current, "NmTicketCell"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getTicketReasonOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.ticketReasonList?.filter((t) => t.IsActive);
              setTicketReasonOpts(sortBy(current, "NmTicketReason"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getTicketStateOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.ticketStateList?.filter((t) => t.IsActive);
              setTicketStateOpts(sortBy(current, "NmTicketState"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getTicketSubReasonOpts({ pIdProject }).then(
            ({ data }) => {
              if (data.idReturnAPI > 0) {
                const current = data.ticketSubReasonList?.filter(
                  (t) => t.IsActive,
                );
                setTicketSubReasonOpts(sortBy(current, "NmTicketSubReason"));
              } else warning({ description: t(aytyFormatError(data)) });
            },
          ),
        ]).catch(errorsResolver.defaultError);

      if (user?.hasAccessReport || user?.hasAccessAudit || user?.hasAccessAdmin)
        await Promise.all([
          ListService.getOrderStateOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.orderStateList?.filter((o) => o.IsActive);
              setOrderStateOpts(sortBy(current, "NmOrderState"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
        ]).catch(errorsResolver.defaultError);

      if (user?.hasAccessAudit)
        await Promise.all([
          ListService.getUserAuditOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.userAuditList?.filter((u) => u.IsActive);
              setUserAuditOpts(sortBy(current, "NmUser"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
          ListService.getUserOperatorOpts({ pIdProject }).then(({ data }) => {
            if (data.idReturnAPI > 0) {
              const current = data.userOperatorList?.filter((u) => u.IsActive);
              setUserOperatorOpts(sortBy(current, "NmUser"));
            } else warning({ description: t(aytyFormatError(data)) });
          }),
        ]).catch(errorsResolver.defaultError);
    }

    setLoading(false);
  }, [pIdProject, user, errorsResolver, warning, t]);

  useEffect(() => {
    getProjectLists();
  }, [getProjectLists]);

  return (
    <ProjectListContext.Provider
      value={{
        loading,
        campaignTypeOpts,
        channelOpts,
        channelTypeOpts,
        customTableOpts,
        hsmTemplateOpts,
        orderStateOpts,
        phoneExternalOpts,
        ticketCellOpts,
        ticketReasonOpts,
        ticketStateOpts,
        ticketSubReasonOpts,
        userAuditOpts,
        userOperatorOpts,
      }}
    >
      {children}
    </ProjectListContext.Provider>
  );
};

export const ProjectListProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useProjectList = () => useContext(ProjectListContext);
